/**
 * Read model for ISFR's Request Details tab: what the partner submitted, and
 * what state the agent's checks found each input in.
 *
 * Values come from two places. The curated APFP projection the agent persists
 * at validation_data.complete_api_data.fundRequest (curate_common_apfp_for_ui
 * in ChronosAgentCoreAgents _platform/apfp_curation.py) carries the
 * operational fields: ids, dates, the account id, the amounts. The curator
 * emits a field only when APFP sent it, so an absent field means APFP did not
 * send it. The checks' stored evidence carries the rest (which documents were
 * attached, the activity name, the description's length) and each input's
 * state. Free text such as the business description itself is not stored, so
 * it is described, not shown.
 *
 * A state comes only from what a check recorded, never from guessing at the
 * value: the documents check's status for each input (present, the right type,
 * readable, with the partner's file names and the check's own reason), its list
 * of duplicated documents, and the field checks' issue codes. Check ids are
 * internal keys here and never shown.
 *
 * Every value is one of: sent and readable (formatted), not sent by APFP
 * (NOT_SENT), not given by the partner (NOT_PROVIDED), not saved on this
 * record because its checks' evidence is not stored (NOT_SAVED), or sent in a
 * shape this view does not recognise (an `{unreadable: <raw>}` marker, shown
 * raw). Nothing is filled in: an unparseable date is not turned into 1970 and a
 * missing amount is not turned into 0.
 *
 * Money arrives as APFP's {units, nanos, currency}. Dates arrive as
 * {timestamp: <epoch ms>} or a bare epoch-ms number (the agent's parser
 * accepts both), passed through unchanged by the curator. An ISO date string is
 * accepted too.
 */
import { firstLine, readChecks } from './reviewRecord';
import {
  INPUT_STATE, INPUT_STATUS, formatValue, humanize, inputEnforced, inputKind, shownName,
} from './checkEvidence';

export { INPUT_STATE };

export const NOT_SENT = null;
export const NOT_PROVIDED = { notProvided: true };
export const NOT_SAVED = { notSaved: true };
/** A field the review does not receive yet and will show once it does. */
export const NOT_YET = { notYet: true };
export const ATTACHED_UNNAMED = 'Attached (file name not recorded)';

/** Marks a value APFP sent that this view cannot read. */
export const unreadable = (raw) => ({ unreadable: raw });

export const isUnreadable = (value) =>
  value !== null && typeof value === 'object' && 'unreadable' in value;

const finiteNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

/** {units, nanos, currency} -> "USD 5,400" or "USD 5,400.50". */
export function formatMoney(money) {
  if (money === undefined || money === null) return NOT_SENT;
  if (typeof money !== 'object' || Array.isArray(money)) return unreadable(money);
  const units = money.units === undefined ? 0 : finiteNumber(money.units);
  const nanos = money.nanos === undefined ? 0 : finiteNumber(money.nanos);
  if ((money.units === undefined && money.nanos === undefined) || units === null || nanos === null) {
    return unreadable(money);
  }
  const amount = units + nanos / 1e9;
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return money.currency ? `${money.currency} ${formatted}` : formatted;
}

/** {timestamp: ms}, a bare epoch-ms number, or an ISO string -> "Nov 22, 2024" (UTC). */
export function formatDate(value) {
  if (value === undefined || value === null) return NOT_SENT;
  let date = null;
  if (typeof value === 'number') {
    date = Number.isFinite(value) ? new Date(value) : null;
  } else if (typeof value === 'object' && !Array.isArray(value)) {
    const ms = finiteNumber(value.timestamp);
    date = ms === null ? null : new Date(ms);
  } else if (typeof value === 'string' && value.trim() !== '') {
    date = new Date(value);
  }
  if (!date || Number.isNaN(date.getTime())) return unreadable(value);
  return date.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

const text = (value) => {
  if (value === undefined || value === null) return NOT_SENT;
  if (typeof value === 'object') return unreadable(value);
  const s = String(value).trim();
  return s === '' ? NOT_SENT : s;
};

const state = (kind, stateText, extra = {}) => ({ kind, text: stateText, ...extra });

/** The short status a state leads with, by kind, where the state names none of its own. */
const DEFAULT_SHORT = {
  [INPUT_STATE.ISSUE]: 'Issue found',
  [INPUT_STATE.MISSING]: 'Missing',
  [INPUT_STATE.NOT_REQUIRED]: 'Not required',
};

/**
 * A state as the row shows it: a short colored status first, the same few words
 * wherever it appears so the page scans down one column, then, on its own line,
 * the explanation when there is one. A passing or not-required state says all it
 * needs in the short status; an issue or a missing input carries its reason.
 */
export function stateLine(rowState) {
  const short = rowState.short || DEFAULT_SHORT[rowState.kind] || rowState.text;
  const explains = rowState.kind === INPUT_STATE.ISSUE || rowState.kind === INPUT_STATE.MISSING;
  const note = explains && rowState.text && rowState.text !== short ? rowState.text : null;
  return { short, note };
}

// Which input each of the field checks' issue codes is about, and which codes
// mean the input is absent rather than present with a problem. The codes are
// the agent's partner-clause keys (field_issue_clauses in the review criteria).
const CODE_INPUT = {
  start_date_missing: 'planned_start',
  end_date_missing: 'planned_end',
  end_not_after_start: 'planned_end',
  activity_name_missing: 'activity_name',
  activity_name_convention: 'activity_name',
  account_id_missing: 'account_id',
  account_id_format: 'account_id',
  business_description_short: 'business_description',
  budget_year_off: 'budget_year',
  calculator_data_missing: 'calculator',
  calculator_url_domain: 'calculator',
  calculator_url_not_shareable: 'calculator',
  design_win_field_required: 'design_win_activity',
};
const ABSENT_CODES = new Set([
  'start_date_missing', 'end_date_missing', 'activity_name_missing', 'account_id_missing',
  'calculator_data_missing', 'design_win_field_required',
]);

// The checks whose issues are about single submission fields. Each records an
// issue's reviewer text and its code side by side, so they pair by position.
const FIELD_CHECKS = ['1.5', '3.1'];

/**
 * Every field issue the field checks recorded, by the input it is about. An
 * issue whose code this view does not know is kept under `other`, so a new
 * code shows up rather than vanishing.
 */
function fieldIssues(checksById) {
  const byInput = new Map();
  const add = (input, code, issueText) => {
    const list = byInput.get(input) || [];
    if (!list.some((i) => i.code === code)) list.push({ code, text: issueText });
    byInput.set(input, list);
  };
  FIELD_CHECKS.forEach((id) => {
    const evidence = (checksById.get(id) || {}).evidence || {};
    const codes = Array.isArray(evidence.field_issue_codes) ? evidence.field_issue_codes : [];
    const texts = Array.isArray(evidence.issues) && evidence.issues.length === codes.length
      ? evidence.issues : [];
    codes.forEach((code, i) => add(CODE_INPUT[code] || 'other', code, texts[i] || humanize(code)));
  });
  return byInput;
}

/** An input's state from its field issues, or `okState` when a check looked and found none. */
function fromIssues(issues, okState) {
  if (!issues || issues.length === 0) return okState;
  const kind = issues.some((i) => ABSENT_CODES.has(i.code)) ? INPUT_STATE.MISSING : INPUT_STATE.ISSUE;
  return state(kind, issues.map((i) => i.text).join('; '));
}

const NOT_ENFORCED = 'Not enforced yet, so it does not affect the decision.';

/** The documents check's evidence, or null when it is not stored in its usual shape. */
const documentsEvidence = (checksById) => {
  const evidence = (checksById.get('1.4') || {}).evidence;
  return evidence && Array.isArray(evidence.missing) ? evidence : null;
};

/** The inputs the documents check reports on, in the order a reviewer reads them. */
export const INPUT_SLOTS = [
  { key: 'calculator', label: 'Pricing calculator' },
  { key: 'innovation_plan', label: 'Innovation plan' },
  { key: 'architecture_diagram', label: 'Architecture diagram' },
];

const stringList = (value) => (Array.isArray(value) ? value.filter((v) => typeof v === 'string' && v !== '') : []);

/**
 * Each submitted input's state, from the documents check's `input_status`: whether
 * it is present, the right type and readable, the partner's file names, which
 * source the review used and the check's own reason. A status this view does not
 * know is still shown, as an issue under its own name.
 *
 * @returns {{status: 'absent'|'ok', inputs: object[]}} 'absent' when the record
 *   carries no input status.
 */
export function readInputs(record) {
  const checks = readChecks(record);
  const doc = documentsEvidence(checks.byId);
  const statuses = doc && doc.input_status && typeof doc.input_status === 'object' ? doc.input_status : null;
  if (!statuses) return { status: 'absent', inputs: [] };
  const inputs = INPUT_SLOTS.filter((slot) => statuses[slot.key] && typeof statuses[slot.key] === 'object')
    .map((slot) => {
      const entry = statuses[slot.key];
      const known = INPUT_STATUS[entry.status];
      return {
        key: slot.key,
        label: slot.label,
        status: entry.status,
        kind: inputKind(entry.status),
        short: known ? known.short : `Unrecognised state: ${entry.status || 'none'}`,
        reason: typeof entry.reason === 'string' ? entry.reason.trim() : '',
        required: entry.required === true,
        files: stringList(shownName(entry, 'files')),
        source: entry.source || null,
        reviewedFile: typeof shownName(entry, 'reviewed_file') === 'string' ? shownName(entry, 'reviewed_file') : null,
        linkBroken: entry.link_broken === true,
        enforced: inputEnforced(slot.key, doc),
        duplicated: Array.isArray(doc.conflicts) && doc.conflicts.includes(slot.key),
      };
    });
  return { status: 'ok', inputs };
}

/**
 * Whether a row's value only repeats what its state says: nothing was provided,
 * and the state already says the input is missing or not required. The view then
 * shows the state alone rather than saying it twice.
 */
export const valueRepeatsState = (item) => item.value === NOT_PROVIDED && Boolean(item.state)
  && (item.state.kind === INPUT_STATE.MISSING || item.state.kind === INPUT_STATE.NOT_REQUIRED);

/** One input as a Request Details row: what was submitted, and the state the check found. */
function inputRow(input, doc, checksById, calculatorIssues) {
  // A readable input names the file the review read; another file attached beside
  // it is the partner's to fix, and the check's reason says which and why.
  const others = input.reviewedFile ? input.files.filter((name) => name !== input.reviewedFile) : [];
  let value = input.status === 'ok' && input.reviewedFile ? input.reviewedFile : null;
  if (!value) value = input.files.length > 0 ? input.files.join('\n') : NOT_PROVIDED;
  let detail;
  const notes = [];
  if (input.kind !== INPUT_STATE.OK && input.kind !== INPUT_STATE.NOT_REQUIRED) notes.push(input.reason || input.short);
  // The reason leads with the state the row already shows ("Readable."), so the rest is used.
  if (input.status === 'ok' && others.length > 0) notes.push(input.reason.replace(new RegExp(`^${input.short}\\.\\s*`), '') || input.reason);
  if (input.duplicated) notes.push(`More than one attached; submit exactly one.${doc.enforcement_enabled ? '' : ` ${NOT_ENFORCED}`}`);
  if (input.key === 'calculator') {
    if (input.status === 'ok') {
      if (doc.calc_dual_source) value = 'File and calculator link (the link was used)';
      else if (input.source === 'link') value = 'Calculator link';
      else if (input.reviewedFile) value = input.reviewedFile;
      const format = (checksById.get('2.11') || {}).evidence || {};
      detail = [
        format.format && formatValue(format.format, 'text', { pricing_calculator: 'AWS Pricing Calculator export' }),
        typeof format.line_item_count === 'number' && `${format.line_item_count} line items`,
        typeof format.total_monthly === 'number' && `${formatValue(format.total_monthly, 'money')} a month`,
      ].filter(Boolean).join(' · ') || undefined;
    }
    if (doc.calc_source_conflict) notes.push('The file and the link disagree on the total or the services.');
    (calculatorIssues || []).forEach((i) => notes.push(i.text));
  }
  // Attached, but the check recorded no file name: say so, rather than "not provided"
  // beside a state that says it was read.
  if (value === NOT_PROVIDED && (input.kind === INPUT_STATE.OK || input.kind === INPUT_STATE.ISSUE)) {
    value = ATTACHED_UNNAMED;
  }
  // A readable input with a note on top (a duplicate, two calculator sources that
  // disagree) leads with what it is and says there is an issue.
  const withNote = input.kind === INPUT_STATE.OK && notes.length > 0;
  const kind = withNote ? INPUT_STATE.ISSUE : input.kind;
  const short = withNote ? `${input.short}, with an issue` : input.short;
  return {
    label: input.label,
    value,
    detail,
    state: state(kind, notes.join(' ') || input.reason || input.short, {
      short, ...(kind === INPUT_STATE.MISSING ? { enforced: input.enforced } : {}),
    }),
  };
}

const TIER_LABELS = { ISFR_LITE: 'ISFR Lite', ISFR_STANDARD: 'ISFR Standard' };

/**
 * How the credit is issued, when it is split across more than one code: the
 * codes' count and value multiply to the requested amount, so a single code only
 * repeats it.
 */
function codesDetail(fundRequest) {
  const count = finiteNumber(fundRequest.numberOfCodes);
  const value = formatMoney(fundRequest.valuePerCode);
  if (count === null || count <= 1 || typeof value !== 'string') return undefined;
  return `Issued as ${count.toLocaleString('en-US')} codes of ${value}`;
}

/**
 * The tab's groups, each a list of {label, value, detail?, state}. `value` is a
 * display string or one of the markers above; `state` is {kind, text} as a
 * check found the input, or null where no check judges it or its evidence is
 * not stored.
 *
 * @returns {{status: 'absent'|'ok', fundRequest: object, checksStatus: string,
 *   groups: object[]}}
 */
export function readRequestDetails(record) {
  const projected = record?.validation_data?.complete_api_data?.fundRequest;
  const fundRequest = projected && typeof projected === 'object' ? projected : {};
  const checks = readChecks(record);
  if (Object.keys(fundRequest).length === 0 && checks.status !== 'ok') {
    return { status: 'absent', fundRequest: {}, checksStatus: checks.status, groups: [] };
  }
  const byId = checks.byId;
  const evidenceOf = (id) => (byId.get(id) || {}).evidence || null;
  // A value read from a check's evidence: not saved when that evidence is not
  // on the record, not provided when the evidence shows the partner gave none.
  const saved = (value, evidencePresent) => {
    if (checks.status !== 'ok' || !evidencePresent) return NOT_SAVED;
    if (value === undefined || value === null || value === '') return NOT_PROVIDED;
    return value;
  };
  const partner = fundRequest.partner || {};
  const opportunity = fundRequest.opportunity || {};
  const project = fundRequest.project || {};
  const issues = fieldIssues(byId);
  const doc = documentsEvidence(byId);
  const fields = (evidenceOf('1.5') || {}).checks || null;
  const template = (evidenceOf('3.1') || {}).fields_present || null;
  const amount = byId.get('1.6');
  const timing = evidenceOf('1.9');
  const ok = (judged, okText) => (judged ? state(INPUT_STATE.OK, okText) : null);

  const startState = (() => {
    const timingFlags = timing && Array.isArray(timing.flags) ? timing.flags : [];
    const found = [...(issues.get('planned_start') || []), ...timingFlags.map((t) => ({ code: 'timing', text: t }))];
    return fromIssues(found, ok(fields, 'Valid'));
  })();

  const descriptionState = template && template.business_description === false
    ? state(INPUT_STATE.MISSING, 'Missing')
    : fromIssues(issues.get('business_description'), ok(fields || template, 'Provided'));

  let designWinRow = { label: 'Design-win activity', value: NOT_SAVED, state: null };
  if (template && typeof template.sandbox_design_win_activity === 'boolean') {
    const required = (issues.get('design_win_activity') || []).length > 0;
    if (template.sandbox_design_win_activity) {
      designWinRow = { ...designWinRow, value: 'Text not stored', state: state(INPUT_STATE.OK, 'Provided') };
    } else {
      designWinRow = {
        ...designWinRow,
        value: NOT_PROVIDED,
        state: required
          ? fromIssues(issues.get('design_win_activity'))
          : state(INPUT_STATE.NOT_REQUIRED, 'Not required for this request'),
      };
    }
  }

  const description = fields && typeof fields.business_description_length === 'number'
    ? `${fields.business_description_length.toLocaleString('en-US')} characters` : undefined;

  let amountState = null;
  if (amount && amount.result === 'PASS' && typeof (amount.evidence || {}).cap === 'number') {
    amountState = state(INPUT_STATE.OK, `Within the ${formatValue(amount.evidence.cap, 'money').replace(/\.00$/, '')} cap`);
  } else if (amount && amount.result === 'FAIL') {
    amountState = state(INPUT_STATE.ISSUE, firstLine(amount.details) || 'Over the cap for this tier', { short: 'Over the cap' });
  }
  const amountEvidence = (amount && amount.evidence) || {};

  const submissionItems = [
    {
      label: 'Activity name',
      value: saved(fields && fields.activity_name, Boolean(fields)),
      state: fromIssues(issues.get('activity_name'), ok(fields, 'Follows the naming convention')),
    },
    { label: 'Business description', value: saved(description, Boolean(fields)), state: descriptionState },
    designWinRow,
    {
      label: 'AWS account ID',
      value: project.awsAccountId !== undefined ? text(project.awsAccountId) : saved(fields && fields.account_id, Boolean(fields)),
      state: fromIssues(issues.get('account_id'), ok(fields, 'Valid 12-digit format')),
    },
    { label: 'Planned start', value: formatDate(project.plannedStartDate), state: startState },
    {
      label: 'Planned end',
      value: formatDate(project.plannedEndDate),
      state: fromIssues(issues.get('planned_end'), ok(fields, 'Valid')),
    },
    {
      label: 'Project length',
      value: saved(fields && typeof fields.project_duration_days === 'number'
        ? formatValue(fields.project_duration_days, 'days') : undefined, Boolean(fields)),
      state: null,
    },
    {
      label: 'Budget year',
      value: saved(fields && fields.budget_year !== undefined ? String(fields.budget_year) : undefined, Boolean(fields)),
      state: fromIssues(issues.get('budget_year'), ok(fields, 'Current or adjacent year')),
    },
  ];
  const other = issues.get('other') || [];
  if (other.length > 0) {
    submissionItems.push({
      label: 'Other submission issues',
      value: other.map((i) => i.text).join('; '),
      state: state(INPUT_STATE.ISSUE, 'Issue found'),
    });
  }

  const inputs = readInputs(record);
  const documentItems = inputs.status === 'ok'
    ? inputs.inputs.map((input) => inputRow(input, doc, byId, issues.get('calculator')))
    : INPUT_SLOTS.map((slot) => ({ label: slot.label, value: NOT_SAVED, state: null }));
  if (doc) {
    documentItems.push(doc.extras_count > 0
      ? {
        label: 'Other files',
        value: `${doc.extras_count} not required`,
        state: state(INPUT_STATE.ISSUE, `Submit only the required documents.${doc.enforcement_enabled ? '' : ` ${NOT_ENFORCED}`}`),
      }
      : { label: 'Other files', value: 'None', state: null });
  }

  // A "looks fine" state needs something to look at: a row with nothing provided
  // keeps no OK state, since no check judged a value that is not there.
  const judged = (items) => items.map((item) => (item.value === NOT_PROVIDED && item.state
    && item.state.kind === INPUT_STATE.OK ? { ...item, state: null } : item));
  return {
    status: 'ok',
    fundRequest,
    checksStatus: checks.status,
    groups: [
      {
        title: 'Submitted documents',
        items: documentItems,
      },
      {
        title: 'Submission fields',
        description: 'Free text such as the business description is not stored on the record, so it is described here rather than shown.',
        items: judged(submissionItems),
      },
      {
        title: 'Credit',
        items: [
          {
            label: 'Requested credit',
            value: formatMoney(fundRequest.requestedCreditAmount),
            detail: codesDetail(fundRequest),
            state: amountState,
          },
          {
            label: 'Tier',
            value: saved(amountEvidence.tier && (TIER_LABELS[amountEvidence.tier] || humanize(amountEvidence.tier)), Boolean(amount)),
            state: null,
          },
          {
            label: 'Strategic Collaboration Agreement',
            value: saved(typeof amountEvidence.is_sca === 'boolean' ? formatValue(amountEvidence.is_sca) : undefined, Boolean(amount)),
            state: null,
          },
        ],
      },
      {
        title: 'Request',
        items: [
          { label: 'Fund request ID', value: text(fundRequest.fundRequestId), state: null },
          { label: 'Benefit application ID', value: text(fundRequest.benefitApplicationId), state: null },
          { label: 'APFP status', value: text(fundRequest.status), state: null },
          { label: 'Program', value: text(fundRequest.program), state: null },
          { label: 'Sub-program', value: text(fundRequest.subProgram), state: null },
          { label: 'Submitted for approval', value: formatDate(fundRequest.dateSubmittedForApproval), state: null },
        ],
      },
      {
        title: 'Partner',
        items: [
          { label: 'Partner', value: text(partner.name), state: null },
          { label: 'SPMS ID', value: text(partner.spmsId), state: null },
          {
            label: 'ACE validation stage',
            value: opportunity.aceValidationStage === undefined ? NOT_YET : text(opportunity.aceValidationStage),
            state: null,
          },
        ],
      },
    ],
  };
}
